import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * api.ts — Administración y Auditoría. Módulo 17.
 *
 * **Sesenta y siete rutas y cien llamadas**: es el archivo que más contrato
 * toca del portal, y el último que quedaba en vanilla junto con Auditoría.
 *
 * Está ordenado por sección de la pantalla, no por router del servidor, porque
 * es como se lee: quien viene aquí busca «lo de Catálogos», no «lo de
 * `catalogs.py`».
 */

/* ── Personas ──────────────────────────────────────────────────────────── */

export type EstadoDeUsuario = 'activos' | 'inactivos' | 'todos';

export interface Usuario {
  id: number;
  cedula: string;
  full_name: string;
  email: string;
  role: string;
  area_id?: number | null;
  area_name?: string | null;
  department?: string | null;
  position?: string | null;
  phone?: string | null;
  city?: string | null;
  is_active: boolean;
  company_id?: number | null;
  sede_id?: number | null;
  position_id?: number | null;
  gender?: string | null;
  status?: string | null;
  work_city?: string | null;
  manager_id?: number | null;
  manager_name?: string | null;
  cost_center?: string | null;
  contract_type_id?: number | null;
  birth_date?: string | null;
  hire_date?: string | null;
  last_login?: string | null;
}

export interface PaginaDeUsuarios {
  total: number;
  items: Usuario[];
}

export interface Area {
  id: number;
  name: string;
  description?: string | null;
  manager_user_id?: number | null;
  manager_name?: string | null;
  /** Si el líder está desactivado, su nombre sigue saliendo y nada parecería
   *  raro. Por eso el servidor manda también si está activo. */
  manager_active?: boolean;
  user_count: number;
  is_active?: boolean;
  /** Reparto por empresa: un área reúne gente de varias del grupo. */
  approvers?: Array<{
    user_id: number;
    user_name: string;
    user_active?: boolean;
    company_id?: number | null;
    company_name?: string | null;
  }>;
  /** Empresas con gente en el área y sin nadie que les firme. */
  sin_cubrir?: string[];
}

export interface EmpresaDelArea {
  id: number;
  name: string;
  en_area?: number;
  activa?: boolean;
}

export interface RepartoDelArea {
  empresas: EmpresaDelArea[];
  approvers: Array<{ user_id: number; company_id: number | null }>;
}

export interface Rol {
  id: number;
  key: string;
  name: string;
  area_scoped?: boolean;
  description?: string | null;
  is_active?: boolean;
  is_staff?: boolean;
  is_admin?: boolean;
  is_system?: boolean;
  permissions?: Record<string, string[]>;
  user_count?: number;
}

export interface ElementoDeCatalogo {
  id: number;
  name: string;
  is_active?: boolean;
  company_id?: number | null;
  [k: string]: unknown;
}

export interface Catalogos {
  companies: ElementoDeCatalogo[];
  sedes: ElementoDeCatalogo[];
  positions: ElementoDeCatalogo[];
  cities: ElementoDeCatalogo[];
  contract_types: ElementoDeCatalogo[];
}

/* ── Claves de caché ───────────────────────────────────────────────────── */

export const claves = {
  todo: ['administracion'] as const,
  usuarios: (busqueda: string, estado: EstadoDeUsuario) =>
    ['administracion', 'usuarios', busqueda, estado] as const,
  usuario: (id: number) => ['administracion', 'usuario', id] as const,
  modulos: (id: number) => ['administracion', 'modulos', id] as const,
  areas: ['administracion', 'areas'] as const,
  mapaDeAprobacion: (areaId: number | null) =>
    ['administracion', 'mapa-aprobacion', areaId] as const,
  roles: ['administracion', 'roles'] as const,
  catalogoDeRoles: ['administracion', 'roles', 'catalogo'] as const,
  catalogos: ['administracion', 'catalogos'] as const,
  panel: ['administracion', 'panel'] as const,
  calidad: ['administracion', 'calidad'] as const,
  plantillas: ['administracion', 'plantillas'] as const,
  moderacion: ['administracion', 'moderacion'] as const,
  palabras: ['administracion', 'palabras'] as const,
  politica: ['administracion', 'politica'] as const,
  historial: ['administracion', 'historial-moderacion'] as const,
  correo: ['administracion', 'correo'] as const,
  marca: ['administracion', 'marca'] as const,
  dominio: ['administracion', 'dominio'] as const,
  copias: ['administracion', 'copias'] as const,
  archivos: ['administracion', 'copias', 'archivos'] as const,
  automatizaciones: ['administracion', 'automatizaciones'] as const,
  eventos: ['administracion', 'automatizaciones', 'eventos'] as const,
  tiposDeEvento: ['administracion', 'automatizaciones', 'tipos'] as const,
  destinosDeImportacion: ['administracion', 'importaciones'] as const,
  legales: ['administracion', 'legales'] as const,
  aceptaciones: (id: number) => ['administracion', 'legales', id, 'aceptaciones'] as const,
  versiones: (id: number) => ['administracion', 'legales', id, 'versiones'] as const,
  auditoria: (filtros: string) => ['administracion', 'auditoria', filtros] as const,
};

function useRefrescar() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: claves.todo });
}

/* ── Usuarios ──────────────────────────────────────────────────────────── */

export function useUsuarios(busqueda: string, estado: EstadoDeUsuario, habilitado: boolean) {
  return useQuery({
    queryKey: claves.usuarios(busqueda, estado),
    queryFn: () => api.get<PaginaDeUsuarios>('/admin/users/', {
      limit: 100, estado, ...(busqueda ? { search: busqueda } : {}),
    }),
    enabled: habilitado,
    placeholderData: (previa) => previa,
  });
}

export function useUsuario(id: number | null) {
  return useQuery({
    queryKey: claves.usuario(id ?? 0),
    queryFn: () => api.get<Usuario>(`/admin/users/${id}`),
    enabled: id != null,
  });
}

export interface CuerpoDeUsuario {
  cedula?: string;
  full_name?: string;
  email?: string;
  area_id?: number | null;
  role?: string;
  birth_date?: string;
  hire_date?: string;
  position?: string | null;
  phone?: string | null;
  city?: string | null;
  [k: string]: unknown;
}

export function useCrearUsuario() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (cuerpo: CuerpoDeUsuario) =>
      api.post<{ id: number; temp_password?: string; message?: string }>('/admin/users/', cuerpo),
    onSuccess: () => refrescar(),
  });
}

export function useAltaRapida() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (cuerpo: CuerpoDeUsuario) =>
      api.post<{ id: number; temp_password: string }>('/admin/users/quick', cuerpo),
    onSuccess: () => refrescar(),
  });
}

export function useGuardarUsuario() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { id: number; cuerpo: CuerpoDeUsuario }) =>
      api.put<{ message: string }>(`/admin/users/${v.id}`, v.cuerpo),
    onSuccess: () => refrescar(),
  });
}

export function useSubirFoto() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { id: number; archivo: File }) => {
      const datos = new FormData();
      datos.append('file', v.archivo);
      return api.upload<{ message: string }>(`/users/${v.id}/avatar`, datos);
    },
    onSuccess: () => refrescar(),
  });
}

/** Activar, inactivar y reactivar son la misma forma: un POST sin cuerpo. */
export function useAccionDeUsuario(accion: 'activate' | 'deactivate' | 'reactivate') {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ message: string }>(`/admin/users/${id}/${accion}`, {}),
    onSuccess: () => refrescar(),
  });
}

export function useRestablecerClave() {
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ temp_password: string; message?: string }>(
        `/admin/users/${id}/reset-password`, {}),
  });
}

export interface ModuloDeUsuario {
  module_key: string;
  is_enabled: boolean;
  /** `role` = lo que le da su rol; `override` = una excepción individual. */
  state: 'role' | 'override';
}

export function useModulosDeUsuario(id: number | null) {
  return useQuery({
    queryKey: claves.modulos(id ?? 0),
    queryFn: () => api.get<ModuloDeUsuario[]>(`/admin/users/${id}/modules`),
    enabled: id != null,
  });
}

export function useGuardarModulos() {
  const refrescar = useRefrescar();
  return useMutation({
    // El cuerpo es la lista tal cual, no un objeto que la envuelva.
    mutationFn: (v: { id: number; modulos: Array<{ module_key: string; is_enabled: boolean }> }) =>
      api.put<{ message: string }>(`/admin/users/${v.id}/modules`, v.modulos),
    onSuccess: () => refrescar(),
  });
}

/** Quitar las excepciones individuales y dejar que mande el rol. */
export function useRestablecerModulos() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (id: number) => api.del<{ message: string }>(`/admin/users/${id}/modules`),
    onSuccess: () => refrescar(),
  });
}

/** Los jefes suplentes de una persona. Viven en su propia tabla, así que se
 *  cargan y se guardan aparte de la ficha. */
export function useAprobadoresDeUsuario(id: number | null) {
  return useQuery({
    queryKey: ['administracion', 'aprobadores', id ?? 0] as const,
    queryFn: () => api.get<{ approvers: Array<{ id: number; full_name: string }> }>(
      `/admin/users/${id}/approvers`),
    enabled: id != null,
  });
}

export function useGuardarAprobadoresDeUsuario() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { id: number; ids: number[] }) =>
      api.put<{ message: string }>(`/admin/users/${v.id}/approvers`, { approver_ids: v.ids }),
    onSuccess: () => refrescar(),
  });
}

export function useSegmentos(id: number | null) {
  return useQuery({
    queryKey: ['administracion', 'segmentos', id ?? 0] as const,
    queryFn: () => api.get<{ tags: string[] }>(`/users/${id}/segments`),
    enabled: id != null,
  });
}

/* ── Áreas ─────────────────────────────────────────────────────────────── */

export function useAreas(habilitado = true) {
  return useQuery({
    queryKey: claves.areas,
    queryFn: () => api.get<Area[]>('/admin/areas/'),
    enabled: habilitado,
    staleTime: 5 * 60_000,
  });
}

export function useGuardarArea() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { id: number | null; cuerpo: Partial<Area> }) =>
      v.id
        ? api.put<{ id?: number; message: string }>(`/admin/areas/${v.id}`, v.cuerpo)
        : api.post<{ id?: number; message?: string }>('/admin/areas/', v.cuerpo),
    onSuccess: () => refrescar(),
  });
}

export function useBorrarArea() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (id: number) => api.del<{ message: string }>(`/admin/areas/${id}`),
    onSuccess: () => refrescar(),
  });
}

export function useRepartoDelArea(areaId: number | null) {
  return useQuery({
    queryKey: ['administracion', 'reparto', areaId ?? 0] as const,
    queryFn: () => api.get<RepartoDelArea>(`/admin/areas/${areaId}/approvers`),
    enabled: areaId != null,
  });
}

export function useGuardarAprobadores() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { areaId: number; filas: Array<{ user_id: number | null; company_id: number | null }> }) =>
      api.put<{ message: string }>(`/admin/areas/${v.areaId}/approvers`,
        // Una fila a medias no significa nada: se descarta antes de enviarla.
        { approvers: v.filas.filter((f) => f.user_id) }),
    onSuccess: () => refrescar(),
  });
}

export interface MapaDeAprobacion {
  total: number;
  sin_aprobador: number;
  filas: Array<{
    nombre: string;
    area: string;
    empresa: string;
    aprobadores: string[];
    /** Por qué vía le aprueban: jefe inmediato, líder de área… o NADIE. */
    via: string;
  }>;
}

export function useMapaDeAprobacion(areaId: number | null, habilitado: boolean) {
  return useQuery({
    queryKey: claves.mapaDeAprobacion(areaId),
    queryFn: () => api.get<MapaDeAprobacion>('/solicitudes/mapa-aprobacion',
      areaId ? { area_id: areaId } : {}),
    enabled: habilitado,
  });
}

/* ── Roles ─────────────────────────────────────────────────────────────── */

export function useRoles(habilitado = true) {
  return useQuery({
    queryKey: claves.roles,
    queryFn: () => api.get<Rol[]>('/admin/roles'),
    enabled: habilitado,
    staleTime: 5 * 60_000,
  });
}

export interface ParDeCatalogo {
  key: string;
  label: string;
}

/** Los módulos y las acciones con los que se arma la matriz de permisos.
 *
 *  Vienen como `{key, label}` y no como cadenas: la etiqueta es la que se lee
 *  («Feed / Comunicaciones») y la clave es la que viaja. */
export function useCatalogoDeRoles(habilitado: boolean) {
  return useQuery({
    queryKey: claves.catalogoDeRoles,
    queryFn: () => api.get<{ modules: ParDeCatalogo[]; actions: ParDeCatalogo[] }>(
      '/admin/roles/catalog'),
    enabled: habilitado,
    staleTime: 30 * 60_000,
  });
}

/** La ficha de un rol CON su matriz de permisos. La lista no la trae. */
export function useRol(id: number | null) {
  return useQuery({
    queryKey: ['administracion', 'rol', id ?? 0] as const,
    queryFn: () => api.get<Rol>(`/admin/roles/${id}`),
    enabled: id != null,
  });
}

export interface CuerpoDeRol {
  name: string;
  description?: string | null;
  is_staff?: boolean;
  area_scoped?: boolean;
  /** La matriz, como la espera el servidor: una fila por módulo. */
  permissions: Array<{ module_key: string; actions: string[] }>;
}

export function useGuardarRol() {
  const refrescar = useRefrescar();
  return useMutation({
    // Por `id`, no por `key`: las rutas del servidor son `/{role_id}` y el POST
    // va a `/admin/roles` SIN barra final —con ella FastAPI redirige y el
    // cuerpo se pierde por el camino—.
    mutationFn: (v: { id: number | null; cuerpo: CuerpoDeRol }) =>
      v.id
        ? api.put<{ message: string }>(`/admin/roles/${v.id}`, v.cuerpo)
        : api.post<{ id: number; key: string; message: string }>('/admin/roles', v.cuerpo),
    onSuccess: () => refrescar(),
  });
}

export function useBorrarRol() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (id: number) => api.del<{ message: string }>(`/admin/roles/${id}`),
    onSuccess: () => refrescar(),
  });
}

/* ── Panel ─────────────────────────────────────────────────────────────── */

export interface Overview {
  colaboradores: {
    activos: number; inactivos: number; hijos_registrados: number; con_genero: number;
    cobertura_empresa_pct: number; cobertura_sede_pct: number; cobertura_cargo_pct: number;
  };
  catalogos: { empresas: number; sedes: number; cargos: number; enumeraciones: number };
  expediente: { subdominios: number; registros: number; colaboradores_con_expediente: number };
  comunicaciones: {
    total: number; enviadas: number; programadas: number; borradores: number;
    destinatarios_alcanzados: number;
    por_canal: { feed: number; email: number | string; notif: number };
  };
  automatizaciones: {
    reglas: number; reglas_activas: number; eventos: number;
    procesados: number; pendientes: number; errores: number;
  };
  importaciones: { total: number; creados: number; actualizados: number; ultima?: string | null };
  trazabilidad: { operaciones: number; por_source: Record<string, number> };
}

export function usePanel(habilitado: boolean) {
  return useQuery({
    queryKey: claves.panel,
    queryFn: () => api.get<Overview>('/analytics/v4/overview'),
    enabled: habilitado,
  });
}

/* ── Catálogos ─────────────────────────────────────────────────────────── */

/** Una lista de catálogo, ya desenvuelta. */
async function lista(ruta: string): Promise<ElementoDeCatalogo[]> {
  return (await api.get<{ items: ElementoDeCatalogo[] }>(ruta)).items ?? [];
}

export function useCatalogos(habilitado = true) {
  return useQuery({
    queryKey: claves.catalogos,
    queryFn: async (): Promise<Catalogos> => {
      // Cinco listas independientes. Van juntas porque la sección las enseña
      // juntas; si una falla, falla la sección — y eso es correcto aquí: sin
      // empresas no se puede configurar nada de lo demás.
      // Ojo con el envoltorio: TODAS estas rutas devuelven `{items: [...]}`,
      // no la lista pelada. Es el contrato de `list_typed` y de `list_items`.
      const [companies, sedes, positions, cities, contract_types] = await Promise.all([
        lista('/admin/catalogs/companies'),
        lista('/admin/catalogs/sedes'),
        lista('/admin/catalogs/positions'),
        lista('/admin/catalogs/items/city'),
        lista('/admin/catalogs/items/contract_type'),
      ]);
      return { companies, sedes, positions, cities, contract_types };
    },
    enabled: habilitado,
    staleTime: 5 * 60_000,
  });
}

export type TipoDeCatalogo = 'companies' | 'sedes' | 'positions' | 'items';

export function useGuardarElementoDeCatalogo() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { tipo: TipoDeCatalogo; id: number | null; cuerpo: Record<string, unknown> }) =>
      v.id
        ? api.put<{ message: string }>(`/admin/catalogs/${v.tipo}/${v.id}`, v.cuerpo)
        : api.post<{ message: string }>(`/admin/catalogs/${v.tipo}`, v.cuerpo),
    onSuccess: () => refrescar(),
  });
}

export function useBorrarElementoDeCatalogo() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { tipo: TipoDeCatalogo; id: number }) =>
      api.del<{ message: string }>(`/admin/catalogs/${v.tipo}/${v.id}`),
    onSuccess: () => refrescar(),
  });
}

export function useSembrarCatalogos() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: () => api.post<{ message: string; created?: Record<string, number> }>(
      '/admin/catalogs/seed-from-data', {}),
    onSuccess: () => refrescar(),
  });
}

/* ── Catálogos, por pestaña ────────────────────────────────────────────── */

/** Los tres catálogos tipados y sus columnas propias. */
export const CATALOGOS_TIPADOS = [
  { clave: 'companies', etiqueta: 'Empresas', extra: 'nit', extraEtiqueta: 'NIT' },
  { clave: 'sedes', etiqueta: 'Sedes', extra: 'city', extraEtiqueta: 'Ciudad' },
  { clave: 'positions', etiqueta: 'Cargos', extra: 'org_level', extraEtiqueta: 'Nivel' },
] as const;

/** Las enumeraciones sueltas (`catalog_items`), en el orden en que se enseñan. */
export const ENUMERACIONES = [
  ['gender', 'Sexo'], ['marital_status', 'Estado civil'],
  ['education_level', 'Nivel educativo'], ['housing_type', 'Tipo de vivienda'],
  ['kinship', 'Parentesco'], ['contract_type', 'Tipo de contrato'],
  ['city', 'Ciudades'],
] as const;

export function useCatalogo(cat: string, habilitado: boolean) {
  return useQuery({
    queryKey: ['administracion', 'catalogo', cat] as const,
    queryFn: () => lista(`/admin/catalogs/${cat}`),
    enabled: habilitado,
  });
}

export function useEnumeracion(tipo: string, habilitado: boolean) {
  return useQuery({
    queryKey: ['administracion', 'enumeracion', tipo] as const,
    queryFn: () => lista(`/admin/catalogs/items/${tipo}`),
    enabled: habilitado,
  });
}

export function useGuardarEnumeracion() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { id: number | null; tipo: string; name: string }) =>
      v.id
        ? api.put<{ message: string }>(`/admin/catalogs/items/${v.id}`, { name: v.name })
        : api.post<{ message: string }>('/admin/catalogs/items',
          { catalog_type: v.tipo, name: v.name }),
    onSuccess: () => refrescar(),
  });
}

export function useBorrarEnumeracion() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (id: number) => api.del<{ message: string }>(`/admin/catalogs/items/${id}`),
    onSuccess: () => refrescar(),
  });
}

/* ── Calidad de datos ──────────────────────────────────────────────────── */

export interface Hallazgo {
  code: string;
  label: string;
  severity: 'error' | 'warning' | 'info';
  count: number;
  hint?: string | null;
  items?: Array<{ id: number; label?: string | null }>;
}

export interface Calidad {
  score: number;
  scanned_users: number;
  total_findings: number;
  summary: Record<string, number>;
  findings: Hallazgo[];
}

export function useCalidad(habilitado: boolean) {
  return useQuery({
    queryKey: claves.calidad,
    queryFn: () => api.get<Calidad>('/analytics/v4/quality'),
    enabled: habilitado,
  });
}

/* ── Moderación ────────────────────────────────────────────────────────── */

export interface PublicacionReportada {
  post_id: number;
  author_name: string;
  content: string;
  report_count: number;
  is_active: boolean;
  reasons?: string[];
}

export interface ComentarioReportado {
  comment_id: number;
  post_id?: number | null;
  author_name: string;
  content: string;
  report_count: number;
  is_active: boolean;
  reasons?: string[];
}

export interface ComentarioOculto {
  comment_id: number;
  post_id?: number | null;
  author_name: string;
  content: string;
  motivo?: string | null;
}

export interface AccionDeModeracion {
  moderator: string;
  action: string;
  action_label: string;
  entity_label: string;
  entity_id: number;
  reason?: string | null;
  created_at: string;
}

export interface PalabraBloqueada {
  id: number;
  word: string;
  de_fabrica?: boolean;
}

/**
 * Las seis listas de Moderación, cada una con su propio estado.
 *
 * El vanilla las pedía con seis `try{}catch{}` VACÍOS seguidos: si la de
 * palabras fallaba, la sección se pintaba con la lista vacía y el mensaje era
 * «La lista está vacía: no se bloquea ninguna palabra» — que es justo lo
 * contrario de lo que había pasado. Aquí cada consulta trae su error y la
 * sección lo dice.
 */
export function useModeracion(habilitado: boolean) {
  // Las seis van escritas una por una, y no en un bucle ni en un ayudante que
  // llame a `useQuery`: el orden de los hooks es parte del contrato de React y
  // un `if` metido dentro de ese ayudante lo rompería sin que nada avisara.
  const publicaciones = useQuery({
    queryKey: ['administracion', 'moderacion', 'posts'] as const,
    queryFn: () => api.get<PublicacionReportada[]>('/admin/moderation/reports'),
    enabled: habilitado,
  });
  const comentarios = useQuery({
    queryKey: ['administracion', 'moderacion', 'comentarios'] as const,
    queryFn: () => api.get<ComentarioReportado[]>('/admin/moderation/comments/reports'),
    enabled: habilitado,
  });
  const ocultos = useQuery({
    queryKey: ['administracion', 'moderacion', 'ocultos'] as const,
    queryFn: () => api.get<ComentarioOculto[]>('/admin/moderation/comments/hidden'),
    enabled: habilitado,
  });
  const palabras = useQuery({
    queryKey: claves.palabras,
    queryFn: () => api.get<PalabraBloqueada[]>('/admin/moderation/words'),
    enabled: habilitado,
  });
  const historial = useQuery({
    queryKey: claves.historial,
    queryFn: () => api.get<AccionDeModeracion[]>('/admin/moderation/history'),
    enabled: habilitado,
  });
  const politica = useQuery({
    queryKey: claves.politica,
    queryFn: () => api.get<{ policy?: string }>('/admin/moderation/policy'),
    enabled: habilitado,
  });
  return { publicaciones, comentarios, ocultos, palabras, historial, politica };
}

/** Ocultar, restaurar y descartar, en publicaciones y comentarios. Es la misma
 *  forma: un POST con el motivo (descartar no lo pide). */
export function useAccionDeModeracion() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: {
      que: 'posts' | 'comments';
      id: number;
      accion: 'hide' | 'restore' | 'dismiss';
      motivo?: string;
    }) => api.post<{ message: string }>(
      `/admin/moderation/${v.que}/${v.id}/${v.accion}`,
      v.accion === 'dismiss' ? null : { reason: v.motivo }),
    onSuccess: () => refrescar(),
  });
}

export function useAgregarPalabra() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (word: string) => api.post<{ message: string }>('/admin/moderation/words', { word }),
    onSuccess: () => refrescar(),
  });
}

export function useBorrarPalabra() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (id: number) => api.del<{ message: string }>(`/admin/moderation/words/${id}`),
    onSuccess: () => refrescar(),
  });
}

/* ── Importaciones ─────────────────────────────────────────────────────── */

export interface DestinoDeImportacion {
  key: string;
  label: string;
  description: string;
  fields: Array<{
    key: string;
    label: string;
    required?: boolean;
    required_create?: boolean;
    catalog?: string | null;
  }>;
}

export const MODOS_DE_IMPORTACION = [
  ['completa', 'Completa', 'crea y actualiza'],
  ['solo_nuevos', 'Solo nuevos', 'ignora los que ya existen'],
  ['solo_actualizar', 'Solo actualizar', 'no crea ninguno'],
] as const;

export interface FilaDeImportacion {
  row: number;
  cedula?: string | null;
  name?: string | null;
  action: string;
  messages?: string[];
}

export interface ResultadoDeImportacion {
  total: number;
  mapped_columns?: string[];
  operation_id?: string | null;
  created?: number;
  updated?: number;
  skipped?: number;
  errors?: number;
  summary?: { create?: number; update?: number; skip?: number; error?: number };
  rows?: FilaDeImportacion[];
}

export function useDestinosDeImportacion(habilitado: boolean) {
  return useQuery({
    queryKey: claves.destinosDeImportacion,
    queryFn: () => api.get<DestinoDeImportacion[]>('/admin/imports/targets'),
    enabled: habilitado,
    staleTime: 30 * 60_000,
  });
}

/** La vista previa y la aplicación son la misma llamada con otra palabra en la
 *  ruta. Se mantienen juntas para que no puedan divergir. */
export function useImportar() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { destino: string; modo: string; archivo: File; aplicar: boolean }) => {
      const datos = new FormData();
      datos.append('file', v.archivo);
      const paso = v.aplicar ? 'commit' : 'preview';
      return api.upload<ResultadoDeImportacion>(
        `/admin/imports/${v.destino}/${paso}?mode=${v.modo}`, datos);
    },
    onSuccess: (_d, v) => { if (v.aplicar) refrescar(); },
  });
}

/* ── Copias y respaldo ─────────────────────────────────────────────────── */

export interface Copia {
  name: string;
  kind: 'db' | 'files' | string;
  created_at?: string | null;
  size?: number | null;
}

export function useCopias(habilitado: boolean) {
  return useQuery({
    queryKey: claves.copias,
    queryFn: () => api.get<Copia[]>('/admin/backups'),
    enabled: habilitado,
  });
}

export function useCrearCopia() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (archivos: boolean) => api.post<{ total_rows?: number; total_objects?: number }>(
      archivos ? '/admin/backups/files' : '/admin/backups', {}),
    onSuccess: () => refrescar(),
  });
}

export function useBorrarCopia() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (nombre: string) => api.del<{ message: string }>(
      `/admin/backups/${encodeURIComponent(nombre)}`),
    onSuccess: () => refrescar(),
  });
}

export function useRestaurarCopia() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { nombre: string; archivos: boolean; motivo: string }) => {
      const paso = v.archivos ? 'restore-files' : 'restore';
      return api.post<{ restored_tables?: number; restored_objects?: number }>(
        `/admin/backups/${encodeURIComponent(v.nombre)}/${paso}`,
        { confirm: 'RESTAURAR', reason: v.motivo });
    },
    onSuccess: () => refrescar(),
  });
}

/* ── Automatizaciones ──────────────────────────────────────────────────── */

export interface TipoDeEvento {
  key: string;
  label: string;
  context?: string[];
}

export interface Regla {
  id: number;
  name: string;
  event_type: string;
  event_label: string;
  mode: string;
  channels?: string[];
  is_active: boolean;
  is_html?: boolean;
  respect_working_hours?: boolean;
  audience_type?: string;
  audience_params?: Record<string, unknown>;
  title_template?: string | null;
  body_template?: string | null;
  template_id?: number | null;
}

export interface EventoDeBitacora {
  event_label: string;
  status: string;
  created_at?: string | null;
  result?: { rules_matched?: number; communications?: unknown[] } | null;
}

export interface OpcionesDeAudiencia {
  areas?: Array<{ id: number; name: string }>;
  roles?: Array<{ key: string; name: string }>;
  companies?: Array<{ id: number; name: string }>;
  sedes?: Array<{ id: number; name: string }>;
  positions?: Array<{ id: number; name: string }>;
  tags?: string[];
}

export const MODOS_DE_REGLA = [
  ['auto', 'Automático (envía)'],
  ['semi', 'Semi (revisar)'],
  ['manual', 'Manual (borrador)'],
] as const;

export const CANALES_DE_REGLA = [
  ['feed', 'Muro'], ['email', 'Correo'], ['notif', 'Buzón'],
] as const;

export const AUDIENCIAS_DE_REGLA = [
  ['subject', 'El colaborador del evento'],
  ['todos_activos', 'Todos los activos'],
  ['por_area', 'Por área'],
  ['por_rol', 'Por rol'],
  ['por_empresa', 'Por empresa'],
  ['por_sede', 'Por sede'],
  ['por_cargo', 'Por cargo'],
  ['por_etiqueta', 'Por etiqueta'],
] as const;

/**
 * Qué campo de `audience_params` usa cada audiencia, y de qué lista salen sus
 * opciones.
 *
 * En el vanilla esto estaba repartido en seis `if` dentro de TRES funciones
 * distintas —una para pintar el desplegable, otra para leer el valor guardado y
 * otra para armar el cuerpo al guardar— y las tres tenían que decir lo mismo a
 * mano. Aquí se declara una vez.
 */
export const PARAMETRO_DE_AUDIENCIA: Record<string,
  { campo: string; lista: keyof OpcionesDeAudiencia; numerico: boolean }> = {
  por_area: { campo: 'area_id', lista: 'areas', numerico: true },
  por_rol: { campo: 'role', lista: 'roles', numerico: false },
  por_empresa: { campo: 'company_id', lista: 'companies', numerico: true },
  por_sede: { campo: 'sede_id', lista: 'sedes', numerico: true },
  por_cargo: { campo: 'position_id', lista: 'positions', numerico: true },
  por_etiqueta: { campo: 'tag', lista: 'tags', numerico: false },
};

export function useAutomatizaciones(habilitado: boolean) {
  const tipos = useQuery({
    queryKey: claves.tiposDeEvento,
    queryFn: () => api.get<TipoDeEvento[]>('/admin/automations/event-types'),
    enabled: habilitado,
    staleTime: 30 * 60_000,
  });
  const reglas = useQuery({
    queryKey: claves.automatizaciones,
    queryFn: () => api.get<Regla[]>('/admin/automations/rules'),
    enabled: habilitado,
  });
  const bitacora = useQuery({
    queryKey: claves.eventos,
    queryFn: () => api.get<EventoDeBitacora[]>('/admin/automations/events'),
    enabled: habilitado,
  });
  const audiencias = useQuery({
    queryKey: ['administracion', 'audiencias'] as const,
    queryFn: () => api.get<OpcionesDeAudiencia>('/communications/audience-options'),
    enabled: habilitado,
    staleTime: 5 * 60_000,
  });
  // Las plantillas son OPCIONALES para una regla: si el Centro de
  // Notificaciones no responde, la regla se sigue pudiendo escribir a mano. El
  // vanilla lo resolvía con un `.catch(() => [])`; aquí la consulta falla y el
  // formulario lo dice, en vez de enseñar un desplegable vacío que parece decir
  // que no hay ninguna.
  const plantillas = useQuery({
    queryKey: claves.plantillas,
    queryFn: () => api.get<Array<{ id: number; name: string }>>('/notifications/templates'),
    enabled: habilitado,
    staleTime: 5 * 60_000,
  });
  return { tipos, reglas, bitacora, audiencias, plantillas };
}

export function useRegla(id: number | null) {
  return useQuery({
    queryKey: ['administracion', 'regla', id ?? 0] as const,
    queryFn: () => api.get<Regla>(`/admin/automations/rules/${id}`),
    enabled: id != null,
  });
}

export function useGuardarRegla() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { id: number | null; cuerpo: Record<string, unknown> }) =>
      v.id
        ? api.put<{ message: string }>(`/admin/automations/rules/${v.id}`, v.cuerpo)
        : api.post<{ message: string }>('/admin/automations/rules', v.cuerpo),
    onSuccess: () => refrescar(),
  });
}

export function useBorrarRegla() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (id: number) => api.del<{ message: string }>(`/admin/automations/rules/${id}`),
    onSuccess: () => refrescar(),
  });
}

export function useDispararEvento() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { event_type: string; subject_user_id?: number }) =>
      api.post<{ status: string; result?: { rules_matched?: number; communications?: unknown[] } }>(
        '/admin/automations/fire', v),
    onSuccess: () => refrescar(),
  });
}

/* ── Personalización, correo y dominio ─────────────────────────────────── */

export interface Marca {
  logo_url?: string | null;
  signature_url?: string | null;
  portal_name?: string | null;
  company_name?: string | null;
  nombre_institucional?: string | null;
  login_subtitle?: string | null;
  primary_color?: string | null;
  accent_color?: string | null;
  show_landing?: boolean;
  signature_name?: string | null;
  app_url?: string | null;
  env_app_url?: string | null;
  disconnect_enabled?: boolean;
  work_start_hour?: number;
  work_end_hour?: number;
  work_days?: string | null;
}

export function useMarca(habilitado: boolean) {
  return useQuery({
    queryKey: claves.marca,
    queryFn: () => api.get<Marca>('/admin/branding'),
    enabled: habilitado,
  });
}

export function useGuardarMarca() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (cuerpo: Partial<Marca>) =>
      api.put<{ message: string }>('/admin/branding', cuerpo),
    onSuccess: () => refrescar(),
  });
}

export function useSubirImagenDeMarca() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { cual: 'logo' | 'signature'; archivo: File }) => {
      const datos = new FormData();
      datos.append('file', v.archivo);
      return api.upload<{ url?: string; message?: string }>(
        `/admin/branding/${v.cual}`, datos);
    },
    onSuccess: () => refrescar(),
  });
}

export interface ConfiguracionDeCorreo {
  smtp_host?: string | null;
  smtp_port?: number | null;
  smtp_user?: string | null;
  from_email?: string | null;
  from_name?: string | null;
  test_sent_at?: string | null;
  test_result?: string | null;
}

export function useCorreo(habilitado: boolean) {
  return useQuery({
    queryKey: claves.correo,
    queryFn: () => api.get<ConfiguracionDeCorreo>('/admin/email-config/'),
    enabled: habilitado,
  });
}

export function useGuardarCorreo() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (cuerpo: Record<string, unknown>) =>
      api.put<{ message: string }>('/admin/email-config/', cuerpo),
    onSuccess: () => refrescar(),
  });
}

export function useProbarCorreo() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: () => api.post<{ success: boolean; message?: string }>(
      '/admin/email-config/test', null),
    onSuccess: () => refrescar(),
  });
}

export interface InstruccionesDeDominio {
  app_url: string;
  firewall: string;
  final: string;
  commands: string[];
  dns_record: { tipo: string; nombre: string; valor: string; ttl: string | number; nota: string };
}

export function useGenerarDominio() {
  return useMutation({
    mutationFn: (v: { domain: string; le_email: string; server_ip?: string }) =>
      api.post<InstruccionesDeDominio>('/admin/domain/wizard', v),
  });
}

/* ── Legal y datos ─────────────────────────────────────────────────────── */

export interface DocumentoLegal {
  id: number;
  doc_key: string;
  title: string;
  is_active: boolean;
  is_mandatory: boolean;
  is_sensitive?: boolean;
  current_version?: string | null;
  acceptances: number;
}

export interface VersionLegal {
  version: string;
  content: string;
  is_current?: boolean;
  summary?: string | null;
}

export function useDocumentosLegales(habilitado: boolean) {
  return useQuery({
    queryKey: claves.legales,
    queryFn: () => api.get<DocumentoLegal[]>('/legal/admin/documents'),
    enabled: habilitado,
  });
}

export function useDocumentoLegal(id: number | null) {
  return useQuery({
    queryKey: claves.versiones(id ?? 0),
    queryFn: () => api.get<DocumentoLegal & { versions: VersionLegal[] }>(
      `/legal/admin/documents/${id}`),
    enabled: id != null,
  });
}

export function useGuardarEstadoLegal() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { id: number; is_active: boolean; is_mandatory: boolean }) =>
      api.put<{ message: string }>(`/legal/admin/documents/${v.id}`,
        { is_active: v.is_active, is_mandatory: v.is_mandatory }),
    onSuccess: () => refrescar(),
  });
}

export function usePublicarVersionLegal() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { id: number; version: string; content: string; summary: string }) =>
      api.post<{ message: string }>(`/legal/admin/documents/${v.id}/versions`,
        { version: v.version, content: v.content, summary: v.summary }),
    onSuccess: () => refrescar(),
  });
}

export interface Aceptacion {
  full_name: string;
  email: string;
  version: string;
  accepted: boolean;
  accepted_at?: string | null;
  ip_address?: string | null;
}

export function useAceptaciones(id: number | null) {
  return useQuery({
    queryKey: claves.aceptaciones(id ?? 0),
    queryFn: () => api.get<{ total: number; items: Aceptacion[] }>(
      `/legal/admin/documents/${id}/acceptances`, { limit: 200 }),
    enabled: id != null,
  });
}

/* ── Auditoría ─────────────────────────────────────────────────────────── */

export interface LineaDeAuditoria {
  created_at: string;
  user_name?: string | null;
  action: string;
  action_label?: string | null;
  summary?: string | null;
  entity_type?: string | null;
  entity_id?: number | null;
  entity_label?: string | null;
  reason?: string | null;
  ip_address?: string | null;
}

export function useAuditoria(accion: string, limite: number, habilitado = true) {
  return useQuery({
    queryKey: claves.auditoria(`${accion}|${limite}`),
    queryFn: () => api.get<{ total: number; items: LineaDeAuditoria[] }>(
      '/admin/audit-log/', { limit: limite, ...(accion ? { action: accion } : {}) }),
    enabled: habilitado,
    placeholderData: (previa) => previa,
  });
}
